import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  Switch,
  TextField,
  Paper,
  Grid,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  Send,
  Copy,
  Check,
  ShieldCheck,
  PhoneCall,
  CheckCircle2,
  Bot,
  AtSign,
  Wifi,
  Zap,
  Lightbulb,
  Tv,
  GraduationCap,
  Wallet,
  CreditCard,
  Receipt,
} from 'lucide-react';

const AppleSwitch = styled(Switch)(({ size }) => {
  const isSmall = size === 'small';
  const width = isSmall ? 38 : 46;
  const height = isSmall ? 22 : 26;
  const thumbSize = isSmall ? 18 : 22;
  const translate = isSmall ? 16 : 20;

  return {
    width,
    height,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: thumbSize + 3,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: `translateX(${translate - 3}px)`,
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      transitionDuration: '240ms',
      '&.Mui-checked': {
        transform: `translateX(${translate}px)`,
        color: '#FFFFFF',
        '& + .MuiSwitch-track': {
          backgroundColor: '#34C759',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: thumbSize,
      height: thumbSize,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.12)',
      transition: 'width 200ms ease, transform 200ms ease',
    },
    '& .MuiSwitch-track': {
      borderRadius: height / 2,
      backgroundColor: '#E5E7EB',
      opacity: 1,
      transition: 'background-color 240ms ease',
      boxSizing: 'border-box',
    },
  };
});

export default function TelegramSettingSection({
  onSave,
}) {
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Form states with realistic dummy data
  const [formData, setFormData] = useState({
    enabled: true,
    botUsername: 'SpeedyDataVtuBot',
    botDisplayName: 'SpeedyData Automated Bot',
    welcomeMessage: '👋 Welcome to SpeedyData Automated Telegram Bot!\n\nBuy instant data, airtime, and pay bills at any time directly through Telegram with zero extra apps.\n\nType /start or tap a button below:',
    supportHandle: '@SpeedyDataSupport',
    dialFee: 10,
    completionFee: 20,
    enabledServices: {
      data: true,
      airtime: true,
      electricity: true,
      cable: true,
      exam: true,
      balanceCheck: true,
      fundWallet: true,
      history: true,
    },
  });

  // Read-only parameters provisioned by Super Admin
  const superAdminProvisioned = {
    botToken: '6819204859:AAEq9_pZ81Kx9uR7LmQ3vBnW2TyU4iXoPaM',
    webhookStatus: 'Connected & Healthy (200 OK)',
  };

  const servicesList = [
    {
      key: 'data',
      label: 'Data Bundles',
      desc: 'MTN, Airtel, Glo, 9mobile data',
      icon: <Wifi size={17} />,
      color: '#0071E3',
      bg: '#EFF6FF',
    },
    {
      key: 'airtime',
      label: 'Airtime VTU',
      desc: 'Instant airtime recharge',
      icon: <Zap size={17} />,
      color: '#F59E0B',
      bg: '#FEF3C7',
    },
    {
      key: 'electricity',
      label: 'Electricity Bills',
      desc: 'Prepaid & postpaid tokens',
      icon: <Lightbulb size={17} />,
      color: '#EAB308',
      bg: '#FEFCE8',
    },
    {
      key: 'cable',
      label: 'Cable TV',
      desc: 'DStv, GOtv, Startimes',
      icon: <Tv size={17} />,
      color: '#8B5CF6',
      bg: '#F5F3FF',
    },
    {
      key: 'exam',
      label: 'Exam PINs',
      desc: 'WAEC, NECO, JAMB tokens',
      icon: <GraduationCap size={17} />,
      color: '#EC4899',
      bg: '#FDF2F8',
    },
    {
      key: 'balanceCheck',
      label: 'Check Wallet Balance',
      desc: 'Instant balance inquiry',
      icon: <Wallet size={17} />,
      color: '#10B981',
      bg: '#ECFDF5',
    },
    {
      key: 'fundWallet',
      label: 'Fund Wallet',
      desc: 'Dedicated bank transfer info',
      icon: <CreditCard size={17} />,
      color: '#06B6D4',
      bg: '#ECFEFF',
    },
    {
      key: 'history',
      label: 'Transaction Receipts',
      desc: 'Recent purchase receipts',
      icon: <Receipt size={17} />,
      color: '#6366F1',
      bg: '#EEF2FF',
    },
  ];

  const enabledCount = Object.values(formData.enabledServices).filter(Boolean).length;

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.info(`Copied ${fieldName} to clipboard`);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleServiceToggle = (serviceKey) => {
    setFormData((prev) => ({
      ...prev,
      enabledServices: {
        ...prev.enabledServices,
        [serviceKey]: !prev.enabledServices[serviceKey],
      },
    }));
  };

  const handleEnableAllServices = () => {
    const updated = {};
    servicesList.forEach((s) => {
      updated[s.key] = true;
    });
    setFormData((prev) => ({ ...prev, enabledServices: updated }));
    toast.info('All Telegram services enabled');
  };

  const handleDisableAllServices = () => {
    const updated = {};
    servicesList.forEach((s) => {
      updated[s.key] = false;
    });
    setFormData((prev) => ({ ...prev, enabledServices: updated }));
    toast.info('All Telegram services disabled');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (onSave) {
        await onSave(formData);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 600));
      }
      toast.success('Telegram Bot settings saved successfully!');
    } catch (err) {
      toast.error('Failed to save Telegram Bot settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3.5} sx={{ width: '100%', pb: 6 }}>
      {/* 1. Apple Header Bar without Deactivate button */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2.5}
        >
          {/* Bot Branding */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: '#229ED9',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 18px rgba(34, 158, 217, 0.28)',
                flexShrink: 0,
              }}
            >
              <Send size={28} />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    letterSpacing: '-0.025em',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  Telegram Bot
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.35,
                    borderRadius: '999px',
                    bgcolor: '#ECFDF5',
                    color: '#059669',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                  Active
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.25, fontSize: '0.88rem' }}>
                Telegram Bot API • Automated Inline Top-Ups
              </Typography>
            </Box>
          </Stack>

          {/* Action Button */}
          <Box sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              startIcon={saving && <CircularProgress size={16} color="inherit" />}
              sx={{
                bgcolor: '#0071E3',
                color: '#FFFFFF',
                borderRadius: '999px',
                px: 3.5,
                py: 1.1,
                fontSize: '0.92rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  bgcolor: '#0077ED',
                  boxShadow: 'none',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* 2. Apple Card: Connected Telegram Bot Status */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.25, sm: 2.75 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                bgcolor: '#EFF6FF',
                color: '#0071E3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ShieldCheck size={24} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.04em' }}>
                CONNECTED TELEGRAM BOT
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.25 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', fontSize: '1.15rem' }}>
                  @{formData.botUsername}
                </Typography>
                <Tooltip title="Copy Bot Username">
                  <Button
                    size="small"
                    onClick={() => handleCopy(`@${formData.botUsername}`, 'Username')}
                    sx={{
                      minWidth: 0,
                      p: 0.6,
                      borderRadius: '8px',
                      color: copiedField === 'Username' ? '#059669' : '#6B7280',
                      bgcolor: copiedField === 'Username' ? '#ECFDF5' : '#F9FAFB',
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    {copiedField === 'Username' ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </Tooltip>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.82rem' }}>
                Status: <span style={{ color: '#059669', fontWeight: 700 }}>● {superAdminProvisioned.webhookStatus}</span>
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.85,
              px: 1.75,
              py: 0.75,
              borderRadius: '999px',
              bgcolor: '#ECFDF5',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              color: '#059669',
              fontSize: '0.78rem',
              fontWeight: 700,
            }}
          >
            <CheckCircle2 size={16} />
            Verified &amp; Connected
          </Box>
        </Stack>
      </Paper>

      {/* 3. Apple Card: Bot Operation & Customer Experience */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack spacing={3}>
          {/* Header Row: Title & Online Switch */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', letterSpacing: '-0.015em' }}>
                Bot Operation &amp; Customer Experience
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.85rem' }}>
                Configure channel availability, session service fees, and automated customer responses.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                px: 1.75,
                borderRadius: '999px',
                bgcolor: formData.enabled ? '#F0FDF4' : '#F9FAFB',
                border: '1px solid',
                borderColor: formData.enabled ? 'rgba(34, 197, 94, 0.25)' : 'rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: formData.enabled ? '#15803D' : '#6B7280',
                    fontSize: '0.84rem',
                    lineHeight: 1.2,
                  }}
                >
                  {formData.enabled ? 'Channel Online' : 'Channel Paused'}
                </Typography>
                <Typography variant="caption" sx={{ color: formData.enabled ? '#16A34A' : '#9CA3AF', display: 'block', fontSize: '0.7rem' }}>
                  {formData.enabled ? 'Accepting chats & orders' : 'Temporarily suspended'}
                </Typography>
              </Box>
              <AppleSwitch
                disableRipple
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            </Box>
          </Stack>

          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

          {/* Section 1: Dial & Transaction Completion Charges */}
          <Box>
            <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em' }}>
              SESSION &amp; TRANSACTION SERVICE CHARGES
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.82rem', mt: 0.25, mb: 2 }}>
              Set custom service fees charged to customer wallets when they dial/initiate a session and upon transaction completion.
            </Typography>

            <Grid container spacing={2}>
              {/* Dial / Session Fee */}
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    borderColor: 'rgba(0,0,0,0.08)',
                    bgcolor: '#F9FAFB',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        bgcolor: '#EFF6FF',
                        color: '#0071E3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PhoneCall size={16} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                        Dial / Session Initiation Charge
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                        Charged when a customer dials the code or starts a session
                      </Typography>
                    </Box>
                  </Stack>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.dialFee}
                    onChange={(e) => setFormData({ ...formData, dialFee: Math.max(0, Number(e.target.value)) })}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 700, color: '#111827' }}>₦</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>per session</Typography>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{
                      bgcolor: '#FFFFFF',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '1.05rem',
                      },
                    }}
                  />
                </Paper>
              </Grid>

              {/* Completion Fee */}
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    borderColor: 'rgba(0,0,0,0.08)',
                    bgcolor: '#F9FAFB',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        bgcolor: '#ECFDF5',
                        color: '#059669',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle2 size={16} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                        Transaction Completion Charge
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                        Convenience surcharge added upon successful order completion
                      </Typography>
                    </Box>
                  </Stack>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.completionFee}
                    onChange={(e) => setFormData({ ...formData, completionFee: Math.max(0, Number(e.target.value)) })}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 700, color: '#111827' }}>₦</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>per order</Typography>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{
                      bgcolor: '#FFFFFF',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '1.05rem',
                      },
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Pricing Formula Preview Banner */}
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: '12px',
                bgcolor: '#F0F7FF',
                border: '1px solid rgba(0, 113, 227, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#0071E3', fontWeight: 600 }}>
                💡 Customer Fee Formula:
              </Typography>
              <Typography variant="caption" sx={{ color: '#1E40AF' }}>
                Customer pays <strong>₦{formData.dialFee || 0}</strong> on session start + Product Price + <strong>₦{formData.completionFee || 0}</strong> on completion.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

          {/* Section 2: Brand & Support Details */}
          <Box>
            <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em' }}>
              BRAND &amp; SUPPORT DETAILS
            </Typography>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bot Display Name"
                  value={formData.botDisplayName}
                  onChange={(e) => setFormData({ ...formData, botDisplayName: e.target.value })}
                  helperText="Displayed in greeting headers and receipts"
                  placeholder="e.g. SpeedyData Automated Bot"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Bot size={18} color="#6B7280" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Support Telegram Handle"
                  value={formData.supportHandle}
                  onChange={(e) => setFormData({ ...formData, supportHandle: e.target.value })}
                  helperText="Handle provided when customers ask for human support"
                  placeholder="e.g. @SpeedyDataSupport"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AtSign size={18} color="#6B7280" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Welcome & /start Greeting Auto-Responder"
                  value={formData.welcomeMessage}
                  onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                  helperText="Initial interactive message sent automatically when a customer dials in or taps /start"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                      lineHeight: 1.5,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Paper>

      {/* 4. Apple Card: Active Services Matrix */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack spacing={2.5}>
          {/* Header with Title, Count badge and Enable/Disable All */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', letterSpacing: '-0.015em' }}>
                  Active Services on Telegram
                </Typography>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.25,
                    borderRadius: '999px',
                    bgcolor: '#F3F4F6',
                    color: '#4B5563',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                  }}
                >
                  {enabledCount} of {servicesList.length} enabled
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.85rem' }}>
                Toggle which VTU products and self-service menus are available to customers.
              </Typography>
            </Box>

            {/* Quick Batch Actions */}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={handleEnableAllServices}
                sx={{
                  color: '#0071E3',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.4,
                  bgcolor: '#F0F7FF',
                  '&:hover': { bgcolor: '#E0F0FE' },
                }}
              >
                Enable All
              </Button>
              <Button
                size="small"
                onClick={handleDisableAllServices}
                sx={{
                  color: '#6B7280',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.4,
                  bgcolor: '#F3F4F6',
                  '&:hover': { bgcolor: '#E5E7EB' },
                }}
              >
                Disable All
              </Button>
            </Stack>
          </Stack>

          {/* Apple Inset Grouped List of Services */}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              borderColor: 'rgba(0,0,0,0.08)',
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            }}
          >
            {servicesList.map((svc, index) => {
              const isChecked = !!formData.enabledServices[svc.key];
              const isLast = index === servicesList.length - 1;

              return (
                <React.Fragment key={svc.key}>
                  <Box
                    onClick={() => handleServiceToggle(svc.key)}
                    sx={{
                      py: 1.75,
                      px: { xs: 2, sm: 2.75 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      bgcolor: isChecked ? 'transparent' : 'rgba(0,0,0,0.015)',
                      '&:hover': {
                        bgcolor: isChecked ? 'rgba(0, 113, 227, 0.025)' : '#F9FAFB',
                      },
                    }}
                  >
                    {/* Left: Icon + Title & Description */}
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, pr: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '12px',
                          bgcolor: isChecked ? svc.bg : '#F3F4F6',
                          color: isChecked ? svc.color : '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {svc.icon}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isChecked ? '#111827' : '#6B7280',
                            fontSize: '0.94rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {svc.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isChecked ? '#6B7280' : '#9CA3AF',
                            fontSize: '0.82rem',
                            mt: 0.25,
                          }}
                        >
                          {svc.desc}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Right: Apple Switch */}
                    <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                      <AppleSwitch
                        disableRipple
                        checked={isChecked}
                        onChange={() => handleServiceToggle(svc.key)}
                      />
                    </Box>
                  </Box>
                  {!isLast && (
                    <Divider
                      sx={{
                        ml: { xs: 7.25, sm: 8 },
                        borderColor: 'rgba(0,0,0,0.06)',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Paper>
        </Stack>
      </Paper>
    </Stack>
  );
}

TelegramSettingSection.propTypes = {
  onSave: PropTypes.func,
};
